import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, roleLabel, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const INDUSTRIES = ["Manufacturing","Retail","Business Services","Construction","Hospitality","Transportation & Logistics","Real Estate","Healthcare","Technology","Wholesale & Distribution","Staffing","Financial Services","Other"];
const ENTITIES = ["LLC","S-Corporation","C-Corporation","Limited Partnership","Sole Proprietorship"];

const inputCls = "border-0 shadow-none rounded-none px-0 h-9 bg-transparent font-semibold text-[18px] focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[#1F2937]/25";

function LabelRow({ label, hint, children }) {
  return (
    <div className="border-b border-slate-200 pb-2">
      <div className="flex items-center justify-between mb-1">
        <div className="small-caps text-slate-500">{label}</div>
        {hint && <div className="small-caps text-[#1F2937]/30">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

export default function Settings() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "",
    business_name: "", legal_entity: "LLC", ein: "", industry: "Manufacturing",
    years_in_business: "", website: "", business_address: "",
    revenue: "", requested_amount: "", purpose_of_funds: "",
  });

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      phone: user.phone || "",
      business_name: user.business_name || "",
      legal_entity: user.legal_entity || "LLC",
      ein: user.ein || "",
      industry: user.industry || "Manufacturing",
      years_in_business: user.years_in_business ?? "",
      website: user.website || "",
      business_address: user.business_address || "",
      revenue: user.revenue ?? "",
      requested_amount: user.requested_amount ?? "",
      purpose_of_funds: user.purpose_of_funds || "",
    });
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      ["years_in_business", "revenue", "requested_amount"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) delete payload[k];
      });
      await api.patch("/auth/me", payload);
      await refresh();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const isBorrower = user?.role === "borrower";
  const isBroker = user?.role === "broker";

  return (
    <form onSubmit={save} className="max-w-[900px] space-y-14" data-testid="settings-page">
      <header className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="small-caps text-slate-500">Your Profile</div>
          <h1 className="mt-6 font-semibold text-[64px] leading-[0.9] tracking-tight text-[#1F2937]">
            Complete your <span className="italic">details.</span>
          </h1>
          <p className="mt-4 max-w-lg text-[15.5px] text-slate-600 leading-relaxed">
            Fill in your account and business information here. These details are
            pulled automatically into every application you file — so you only
            enter them once.
          </p>
        </div>
        <button type="submit" disabled={busy} className="group inline-flex items-center gap-4 disabled:opacity-40" data-testid="settings-save-btn">
          <span className="h-[1px] w-16 bg-slate-800 transition-all group-hover:w-24" />
          <span className="small-caps text-[#1F2937]">{busy ? "Saving…" : "Save Changes"}</span>
        </button>
      </header>

      <section>
        <div className="small-caps text-slate-500 mb-6">Account</div>
        <div className="grid grid-cols-2 gap-x-12 gap-y-8">
          <LabelRow label="Full Name">
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="set-name" className={inputCls} />
          </LabelRow>
          <LabelRow label="Email" hint="Contact support to change">
            <Input value={user?.email || ""} readOnly className={`${inputCls} opacity-70`} />
          </LabelRow>
          <LabelRow label="Phone">
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="set-phone" className={inputCls} placeholder="+1 (555) 000-0000" />
          </LabelRow>
          <LabelRow label="Role">
            <div className="font-semibold text-[18px] text-[#1F2937] py-1.5">{roleLabel(user?.role)}</div>
          </LabelRow>
        </div>
      </section>

      {isBorrower && (
        <>
          <section>
            <div className="small-caps text-slate-500 mb-6">Business</div>
            <div className="grid grid-cols-2 gap-x-12 gap-y-8">
              <LabelRow label="Business Name">
                <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="set-business-name" className={inputCls} />
              </LabelRow>
              <LabelRow label="Legal Entity">
                <Select value={form.legal_entity} onValueChange={(v) => set("legal_entity", v)}>
                  <SelectTrigger data-testid="set-legal-entity" className="border-0 shadow-none rounded-none px-0 h-9 bg-transparent font-semibold text-[18px] focus:ring-0"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENTITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </LabelRow>
              <LabelRow label="EIN">
                <Input value={form.ein} onChange={(e) => set("ein", e.target.value)} data-testid="set-ein" className={inputCls} placeholder="00-0000000" />
              </LabelRow>
              <LabelRow label="Industry">
                <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                  <SelectTrigger data-testid="set-industry" className="border-0 shadow-none rounded-none px-0 h-9 bg-transparent font-semibold text-[18px] focus:ring-0"><SelectValue /></SelectTrigger>
                  <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </LabelRow>
              <LabelRow label="Years in Business">
                <Input type="number" value={form.years_in_business} onChange={(e) => set("years_in_business", e.target.value)} data-testid="set-years" className={inputCls} />
              </LabelRow>
              <LabelRow label="Website">
                <Input value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="set-website" className={inputCls} placeholder="https://" />
              </LabelRow>
              <div className="col-span-2">
                <LabelRow label="Business Address">
                  <Input value={form.business_address} onChange={(e) => set("business_address", e.target.value)} data-testid="set-address" className={inputCls} placeholder="Street, City, State ZIP" />
                </LabelRow>
              </div>
            </div>
          </section>

          <section>
            <div className="small-caps text-slate-500 mb-6">Financing Snapshot</div>
            <div className="grid grid-cols-2 gap-x-12 gap-y-8">
              <LabelRow label="Annual Revenue (USD)">
                <Input type="number" value={form.revenue} onChange={(e) => set("revenue", e.target.value)} data-testid="set-revenue" className={inputCls} placeholder="0" />
              </LabelRow>
              <LabelRow label="Requested Amount (USD)">
                <Input type="number" value={form.requested_amount} onChange={(e) => set("requested_amount", e.target.value)} data-testid="set-requested" className={inputCls} placeholder="0" />
              </LabelRow>
              <div className="col-span-2">
                <LabelRow label="Purpose of Funds">
                  <Textarea rows={4} value={form.purpose_of_funds} onChange={(e) => set("purpose_of_funds", e.target.value)} data-testid="set-purpose"
                    className="border-0 shadow-none rounded-none px-0 font-semibold text-[16px] bg-transparent focus-visible:ring-0 resize-none min-h-[80px]"
                    placeholder="How will you use the capital?" />
                </LabelRow>
              </div>
            </div>
          </section>
        </>
      )}

      {isBroker && (
        <section>
          <div className="small-caps text-slate-500 mb-6">Advisory Firm</div>
          <div className="grid grid-cols-2 gap-x-12 gap-y-8">
            <LabelRow label="Firm Name">
              <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="set-firm" className={inputCls} />
            </LabelRow>
            <LabelRow label="Website">
              <Input value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="set-website" className={inputCls} placeholder="https://" />
            </LabelRow>
          </div>
        </section>
      )}

      <div className="border-t border-slate-200 pt-8 flex items-center justify-between">
        <div className="small-caps text-slate-500">
          Saved to your account · used across all applications
        </div>
        <button type="submit" disabled={busy} className="group inline-flex items-center gap-4 disabled:opacity-40" data-testid="settings-save-bottom-btn">
          <span className="h-[1px] w-16 bg-slate-800 transition-all group-hover:w-24" />
          <span className="small-caps text-[#1F2937]">{busy ? "Saving…" : "Save Changes"}</span>
        </button>
      </div>
    </form>
  );
}
